import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AppIcon from './AppIcon';
import { BrandLoader } from './ui/BrandLoader';

interface RootState {
  user: { role: string; roles?: string[]; isManager: boolean; firstLoad: boolean; isAssetManager?: boolean };
}

interface RoleProtectedRouteProps {
  allowedRoles: string[];
  children: React.ReactElement;
  /**
   * When true, `allowedRoles` is matched only against the roles the user really holds (`user.roles`,
   * falling back to the primary `role` until loaded): the synthetic 'Manager' (isManager) and
   * 'AssetManagement' tags do not count. Mirrors a server role-membership policy. CO-920 (owner,
   * 2026-10-22): a Clocking user who has direct reports must not reach the employee directory, while
   * their other manager pages keep working. Workspaces uses it the same way (owner 2026-10-10).
   * Pair it with `primaryRoleOnly` on the nav page so the sidebar does not offer a dead-end link.
   */
  primaryRoleOnly?: boolean;
}

// How long the "no access" notice stays on screen before the guard redirects home.
const REDIRECT_DELAY_MS = 2200;

// Hard ceiling on how long we will wait for the role state to hydrate before we
// decide with whatever we have. Only reachable if the boot-time `getUserInfo()`
// in PrivateRoute failed outright (it leaves `firstLoad` stuck at true and never
// retries) — without this the guard would spin forever on that error path.
const MAX_HYDRATION_WAIT_MS = 15000;

/**
 * Route guard that restricts access by the current user's role.
 *
 * Effective roles mirror `useFilteredNavigation`: the user's primary `role`
 * string plus the synthetic `'Manager'` role when `isManager` is set (the user
 * has direct reports). This keeps route access consistent with sidebar
 * visibility — otherwise a people-manager whose primary role is `'Clocking'`
 * would see manager menu items but get bounced when clicking them.
 *
 * When a blocked role reaches a restricted route directly (typed/bookmarked URL),
 * the redirect is no longer silent: a brief "you don't have access" notice is
 * shown before bouncing to the dashboard, so the bounce isn't confusing.
 *
 * The guard has THREE states, never two:
 *   - loading   — the role state has not hydrated yet. `state.user.role` is `""`
 *                 for the whole window between the OIDC callback and the
 *                 `INIT` dispatch that `PrivateRoute`'s `getUserInfo()` feeds.
 *                 An empty role set is NOT a denial, so we render a waiting
 *                 state and make no authorization decision at all.
 *   - authorized — an effective role matched `allowedRoles`.
 *   - denied     — roles ARE known and none matched. Only this state redirects.
 *
 * `firstLoad` is the hydration signal: it starts true, `Callback` re-arms it on
 * every sign-in, and only the `INIT` action (the one that also writes `role` /
 * `isManager`) clears it. So `firstLoad === false` means "this session's role
 * state came from the server", and a page refresh — which rehydrates both
 * `firstLoad: false` and the persisted role together from redux-persist — is
 * known immediately with no extra wait.
 */
export const RoleProtectedRoute = ({ allowedRoles, children, primaryRoleOnly = false }: RoleProtectedRouteProps) => {
  const { t } = useTranslation('common');
  const role = useSelector((state: RootState) => state.user.role);
  const roles = useSelector((state: RootState) => state.user.roles);
  const isManager = useSelector((state: RootState) => state.user.isManager);
  const isAssetManager = useSelector((state: RootState) => state.user.isAssetManager);
  const firstLoad = useSelector((state: RootState) => state.user.firstLoad);

  const [hydrationTimedOut, setHydrationTimedOut] = useState(false);
  const rolesKnown = !firstLoad || hydrationTimedOut;

  const effectiveRoles = new Set([role]);
  if (isManager && !primaryRoleOnly) effectiveRoles.add('Manager');
  if (isAssetManager && !primaryRoleOnly) effectiveRoles.add('AssetManagement');

  // Opt-in: a route whose server policy checks real roles only ignores the synthetic additions.
  const realRoles = Array.isArray(roles) && roles.length > 0 ? roles : [role];
  const allowed = rolesKnown && (primaryRoleOnly
    ? allowedRoles.some(r => realRoles.includes(r))
    : allowedRoles.some(r => effectiveRoles.has(r)));
  const denied = rolesKnown && !allowed;

  const [redirect, setRedirect] = useState(false);

  // Backstop for a boot that never completes (see MAX_HYDRATION_WAIT_MS).
  useEffect(() => {
    if (!firstLoad) return;
    const timer = setTimeout(() => setHydrationTimedOut(true), MAX_HYDRATION_WAIT_MS);
    return () => clearTimeout(timer);
  }, [firstLoad]);

  // The redirect timer starts ONLY on a definite denial. While roles are still
  // unknown no timer exists, so it cannot fire against an un-hydrated role and
  // latch `redirect` permanently.
  useEffect(() => {
    if (!denied) {
      setRedirect(false);
      return;
    }
    const timer = setTimeout(() => setRedirect(true), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [denied]);

  if (allowed) return children;

  if (!rolesKnown) {
    return (
      // Route-level gate, so it uses the app's brand loading identity rather
      // than a generic spinner. `inline` because this renders inside the app
      // shell, which already owns the page background.
      <div className="flex flex-1 items-center justify-center p-6">
        <BrandLoader variant="inline" size="md" />
      </div>
    );
  }

  if (redirect) return <Navigate to="/" replace />;

  return (
    <div role="alert" aria-live="assertive" className="flex flex-1 items-center justify-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AppIcon name="ShieldAlert" size={24} />
        </div>
        <h2 className="text-base font-semibold text-foreground">
          {t("You don't have access to this page")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('Redirecting you to your dashboard…')}
        </p>
        <button
          type="button"
          onClick={() => setRedirect(true)}
          className="mt-1 text-sm font-medium text-primary hover:underline"
        >
          {t('Go to dashboard')}
        </button>
      </div>
    </div>
  );
};
